"use client";

import Link from "next/link";
import { CGU_VERSION_LABEL } from "@/lib/legal";

/**
 * Case d'acceptation des CGU, partagée par les deux seuls écrans qui créent un
 * accès : la demande d'inscription et l'acceptation d'invitation.
 *
 * ── Pourquoi une vraie case, décochée ───────────────────────────────────────
 *
 * Un accord doit être un acte positif et non ambigu : pas de case pré-cochée,
 * pas d'acceptation déduite du seul clic sur « Continuer », pas de formulation
 * qui noie l'engagement dans une phrase de bas de page. `checked` part donc à
 * `false` et rien ne le coche à la place de la personne.
 *
 * ── Pourquoi le lien s'ouvre dans un nouvel onglet ──────────────────────────
 *
 * L'inscription est un formulaire en trois étapes, sans persistance : naviguer
 * vers `/cgu` dans le même onglet effacerait tout ce qui a été saisi, et la
 * leçon apprise serait « ne pas cliquer sur le lien ». Un texte qu'on est puni
 * de lire n'est pas un texte accepté en connaissance de cause.
 *
 * ── Ce que ce composant n'envoie PAS ────────────────────────────────────────
 *
 * La version acceptée. Elle est affichée ici pour information, mais c'est le
 * serveur qui l'estampille depuis sa propre constante : laisser le client la
 * choisir permettrait de prouver l'acceptation de n'importe quel texte.
 */
export function CguConsent({
  checked,
  onChange,
  className = "",
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  className?: string;
}) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-input border border-border bg-bg px-3.5 py-3 ${className}`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-primary"
      />
      <span className="text-[13px] leading-relaxed text-text-muted">
        J&apos;ai lu et j&apos;accepte les{" "}
        <Link
          href="/cgu"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-2"
        >
          conditions générales d&apos;utilisation
        </Link>{" "}
        <span className="text-text-faint">(version du {CGU_VERSION_LABEL})</span>.
      </span>
    </label>
  );
}
